<template>
  <div>
    <div class="mb-3">
      <v-btn
        v-if="companyId"
        small
        text
        color="primary"
        :disabled="disabled || busy || !companyId"
        :loading="configLoading"
        @click="loadDefault"
      >
        Use company default Wi-Fi
      </v-btn>
      <p
        v-if="!companyId"
        class="text-caption"
      >
        Company default Wi-Fi is available only when every device in this group belongs to the same company.
      </p>
    </div>
    <v-switch
      v-model="form.useSavedNetwork"
      label="Use Saved Network"
      :disabled="disabled || busy"
      @change="changeMode"
    />
    <p
      v-if="form.useSavedNetwork"
      class="text-caption"
    >
      Enter an SSID already saved on every device in this group. Saved networks are not fetched for groups.
    </p>
    <v-alert
      v-if="notice"
      dense
      text
      type="info"
    >
      {{ notice }}
    </v-alert>
    <v-alert
      v-if="error"
      dense
      text
      type="error"
    >
      {{ error }}
    </v-alert>
    <v-text-field
      v-model="form.ssid"
      label="SSID *"
      :disabled="disabled || busy"
      hint="1 to 32 UTF-8 bytes; spaces are preserved"
      persistent-hint
      @input="publish"
    />
    <template v-if="!form.useSavedNetwork">
      <v-select
        v-model="form.security"
        :items="securityOptions"
        label="Security *"
        :disabled="disabled || busy"
        @change="changeSecurity"
      />
      <v-text-field
        v-if="form.security !== 'OPEN'"
        v-model="form.password"
        label="Password *"
        type="password"
        :disabled="disabled || busy"
        @input="publish"
      />
      <v-switch
        v-model="form.hidden"
        label="Hidden Network"
        :disabled="disabled || busy"
        @change="publish"
      />
    </template>
    <v-text-field
      v-model.number="form.timeoutSeconds"
      type="number"
      min="5"
      max="120"
      label="Verification Timeout"
      hint="5 to 120 seconds"
      persistent-hint
      :disabled="disabled || busy"
      @input="publish"
    />
    <v-alert
      v-if="showErrors && errors.length"
      dense
      text
      type="error"
    >
      {{ errors.join(' ') }}
    </v-alert>
  </div>
</template>
<script>
  import Wifi from '@/services/WifiManagementDataService'
  import { companyWifiForm, validateWifiConnect, wifiSecurityOptions } from '@/util/wifiManagement'

  const emptyForm = () => ({ ssid: '', password: '', security: 'WPA2', useSavedNetwork: false, hidden: false, networkId: null, timeoutSeconds: 30 })
  export default {
    props: {
      command: { type: Object, required: true },
      groupId: { type: [Number, String], default: null },
      companyId: { type: [Number, String], default: null },
      disabled: Boolean,
      showErrors: Boolean,
    },
    data: () => ({ form: emptyForm(), error: '', notice: '', configLoading: false, generation: 0 }),
    computed: {
      securityOptions () { return wifiSecurityOptions },
      errors () { return validateWifiConnect(this.form, true) },
      busy () { return this.configLoading },
    },
    watch: {
      groupId () { this.reset() },
      companyId () { this.reset() },
    },
    mounted () { this.publish() },
    beforeDestroy () { this.generation++ },
    methods: {
      reset () {
        this.generation++
        this.configLoading = false
        this.form = emptyForm()
        this.error = this.notice = ''
        this.publish()
      },
      publish () {
        const values = {}
        this.command.fields.forEach(field => {
          const key = Object.keys(this.form).find(key => key.toLowerCase() === field.key.toLowerCase())
          if (key) values[field.key] = this.form[key]
        })
        this.$emit('input', values)
        this.$emit('validity', !this.busy && !this.errors.length)
      },
      changeMode () {
        this.notice = ''
        this.form.password = ''
        this.form.networkId = null
        this.publish()
      },
      changeSecurity () { this.form.password = ''; this.publish() },
      message (error) { return error.response?.data?.message || error.response?.data?.title || 'Could not load Wi-Fi settings. Please try again.' },
      async loadDefault () {
        if (!this.companyId || this.busy || this.disabled) return
        const generation = this.generation
        this.configLoading = true
        this.error = ''
        this.publish()
        try {
          const response = await Wifi.companyDefault(this.companyId)
          if (generation !== this.generation) return
          if (!response.data?.success || !response.data.data) throw new Error('No active company default Wi-Fi configuration was found.')
          this.form = { ...emptyForm(), timeoutSeconds: this.form.timeoutSeconds, ...companyWifiForm(response.data.data) }
          this.notice = 'Company default loaded. Review the connection settings before sending.'
        } catch (error) {
          if (generation === this.generation) this.error = error.response ? this.message(error) : error.message
        } finally { if (generation === this.generation) { this.configLoading = false; this.publish() } }
      },
    },
  }
</script>
